import { useMemo } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  Stack,
  Typography
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { Entity, EntityItem, MediaItemDetailed, MediaResponse, RelatedEntityItem } from "../../types";
import { Circle, Warning } from "@mui/icons-material";

type DeleteDialogProps = {
  item: Entity | EntityItem | MediaResponse | MediaItemDetailed | undefined;
  onDelete: () => Promise<void> | void;
  onCancel: () => void;
  open: boolean;
  isPending?: boolean;
};

export default function DeleteDialog({ item, onDelete, onCancel, open, isPending }: DeleteDialogProps) {
  const relationships = useMemo(() => {
    const allRelationships: RelatedEntityItem[] = [];

    if (item?.relationships) {
      // Add all related_entities relationships
      if (item.relationships.related_entities) {
        Object.entries(item.relationships.related_entities).forEach(([_key, entities]) => {
          allRelationships.push(...(entities as RelatedEntityItem[]));
        });
      }

      // Add all geo_entities relationships
      if (item.relationships.geo_entities) {
        Object.entries(item.relationships.geo_entities).forEach(([_key, entities]) => {
          allRelationships.push(...(entities as RelatedEntityItem[]));
        });
      }

      // Add media relationships if they exist
      if (item.relationships.media && Array.isArray(item.relationships.media)) {
        allRelationships.push(...item.relationships.media);
      }
    }
    return allRelationships;
  }, [item]);

  return (
    <Dialog
      open={open}
      PaperProps={{
        sx: {
          borderRadius: "0.5rem",
          paddingBlock: "0.75rem"
        }
      }}
      onClose={onCancel}
    >
      <DialogTitle sx={{ color: "error.main", display: "flex", alignItems: "flex-start", gap: "0.5rem" }}>
        <Warning fontSize='large' />
        <Typography variant='h5' fontWeight={500} sx={{ lineBreak: "anywhere" }}>
          Permanently Delete {item?.name || "Item"}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack gap={"1rem"}>
          <Typography variant='body1' component='div' sx={{ mb: "0.5rem" }}>
            <strong>Warning:</strong> You are about to permanently delete this item from the database.
          </Typography>

          <Box>
            {
              // Collect all relationships from the media item
              // Performance: Delete dialog logging reduced to minimize duplicate output
              relationships?.length > 0 ? (
                <Box sx={{ mb: 2 }}>
                  <Typography variant='subtitle1' gutterBottom component='div'>
                    This item is currently linked to ({relationships?.length} relationship):
                  </Typography>

                  <List sx={{ maxHeight: "10rem", overflowY: "auto" }} className='scroller'>
                    {relationships?.map((item, index) => (
                      <ListItem key={`delete-${item.id}-${index}`} sx={{ paddingBlock: "0.075rem", alignItems: "baseline" }}>
                        <Circle sx={{ fontSize: "0.4rem", color: "grey.400", mr: 12, mt: 3 }} />
                        <Typography variant='subtitle1' color='grey.600'>
                          {item.entity_name || item.name || `ID: ${item.id}`}
                        </Typography>
                      </ListItem>
                    ))}
                  </List>
                  <Typography color='error' sx={{ mt: 2 }} component='div'>
                    All of these relationships will be permanently deleted.
                  </Typography>
                </Box>
              ) : (
                <Typography variant='body2' component='div' sx={{ mb: 2 }}>
                  This item has no current relationships.
                </Typography>
              )
            }
          </Box>

          <Typography
            variant='body1'
            component='div'
            sx={{
              mt: 2,
              p: "0.5rem 0.75rem",
              bgcolor: "error.400",
              color: "error.contrastText",
              borderRadius: 1
            }}
          >
            This action cannot be undone. Are you absolutely sure you want to delete this item?
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ paddingInline: "1.5rem" }}>
        <Button onClick={onCancel} variant='secondary-outlined' disabled={isPending}>
          Cancel
        </Button>
        <Button
          onClick={onDelete}
          color='error'
          variant='contained'
          disableElevation
          startIcon={isPending ? <CircularProgress size={12} sx={{ color: "common.white" }} /> : <DeleteIcon />}
          disabled={isPending}
        >
          Permanently Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
}
